import {
    FREQUENCY_LABELS,
    HIGH_FREQUENCIES,
    symptomCountChange,
    type ProgressReport,
} from '@light2live/shared-types';
import {
    METRICS,
    seriesFor,
    summarise,
    type TrendChange,
    type TrendMetric,
} from '@/lib/progress-trend';
import { DeltaChip, changeLabel, symptomChipLabel } from './DeltaChip';

/**
 * What changed, above everything else (#241).
 *
 * The tracker is the only thing in the product that measures an OUTCOME —
 * everything else counts effort — and until this block both tracker views
 * opened with readings and left the reader to infer the story. The story is the
 * point: 159.7 → 152, energy 6 → 8, six symptoms down to two.
 *
 * Colour is never the only channel. Every chip carries an arrow, a magnitude
 * and a word, because the two brand fills used here (#94c740 and #f6a350)
 * reduce to #b7b7b7 and #b5b5b5 in greyscale — two shades apart, which is to
 * say indistinguishable on a black-and-white screen or print. The word is what
 * survives, so the word is what does the work.
 */
export function ProgressHeadline({ reports }: { reports: ProgressReport[] }) {
    const symptoms = symptomCountChange(reports);
    const metrics = METRICS.map((metric) => ({
        metric,
        change: summarise(seriesFor(reports, metric), metric),
    }));

    // The words the client actually saw on the form, so this line cannot drift
    // from what they ticked if the labels are ever reworded.
    const highWords = HIGH_FREQUENCIES.map((frequency) => FREQUENCY_LABELS[frequency]).join(' or ');

    return (
        <section
            aria-labelledby="headline-heading"
            className="rounded-2xl border border-brand-surface-edge bg-brand-surface p-5 shadow-sm sm:p-6"
        >
            <h2
                id="headline-heading"
                className="text-sm font-semibold tracking-wide text-brand-text-green uppercase"
            >
                What has changed
            </h2>

            {symptoms !== null && (
                <>
                    <p className="mt-3 text-lg leading-snug text-gray-900 sm:text-xl">
                        {symptoms.delta === null ? (
                            <>
                                You reported{' '}
                                <strong className="text-2xl tabular-nums sm:text-3xl">
                                    {symptoms.last}
                                </strong>{' '}
                                of {symptoms.outOf} symptoms as {highWords}.
                            </>
                        ) : (
                            <>
                                Symptoms you reported as {highWords}:{' '}
                                <strong className="text-2xl tabular-nums sm:text-3xl">
                                    {symptoms.first} → {symptoms.last}
                                </strong>{' '}
                                <span className="text-base text-gray-700">of {symptoms.outOf}</span>
                            </>
                        )}
                    </p>

                    {symptoms.delta !== null && (
                        <p className="mt-2">
                            <DeltaChip
                                verdict={symptoms.verdict}
                                delta={symptoms.delta}
                                label={symptomChipLabel(symptoms.delta, symptoms.verdict)}
                            />
                        </p>
                    )}
                </>
            )}

            <dl className="mt-5 grid gap-3 sm:grid-cols-3">
                {metrics.map(({ metric, change }) => (
                    <MetricTile key={metric.key} metric={metric} change={change} />
                ))}
            </dl>
        </section>
    );
}

/**
 * One metric, as first → last with the change beneath it.
 *
 * Every path through this has to survive a client who never recorded the
 * metric: `change.last` is null the whole way down for someone who has never
 * weighed in, and a tile showing "0.0 kg" or a delta of NaN would be inventing
 * a clinical reading. Absent renders as absent.
 */
function MetricTile({ metric, change }: { metric: TrendMetric; change: TrendChange }) {
    return (
        <div className="rounded-xl bg-muted px-4 py-3">
            <dt className="text-xs font-semibold tracking-wide text-gray-700 uppercase">
                {metric.label}
            </dt>

            {change.last === null ? (
                <dd className="mt-1 text-gray-700">Not recorded yet</dd>
            ) : (
                <dd className="mt-1">
                    <span className="flex flex-wrap items-baseline gap-x-2">
                        {change.delta !== null && change.first !== null && (
                            <span className="text-base text-gray-700 tabular-nums">
                                {metric.format(change.first)} →
                            </span>
                        )}
                        <span className="text-3xl font-bold text-gray-900 tabular-nums">
                            {metric.format(change.last)}
                        </span>
                        <span className="text-sm text-gray-700">{metric.unit}</span>
                    </span>

                    {change.delta === null ? (
                        <span className="mt-2 block text-sm text-gray-700">
                            Your starting point
                        </span>
                    ) : (
                        <span className="mt-2 block">
                            <DeltaChip
                                verdict={change.verdict}
                                delta={change.delta}
                                label={changeLabel(metric, change, 'your first report')}
                            />
                        </span>
                    )}
                </dd>
            )}
        </div>
    );
}
